// Blue-noise threshold map for the 2-bit look (docs/plans/dither-2bit.md, Dither types): void-and-cluster (Ulichney
// 1993), made at run time from a fixed seed, so the same map comes out every time and no image is checked in.
// 64 x 64 takes about 100 ms (Node, M1 Pro); it is made once, the first time a room uses the blue-noise dither.

/** Ranks 0..size*size-1 of a size x size tiling blue-noise pattern (row-major), from void-and-cluster. */
export function voidAndCluster(size = 64, sigma = 1.5, seed = 1): Uint16Array {
  const n = size * size;
  // toroidal Gaussian kernel by offset
  const k = new Float64Array(n);
  for (let y = 0; y < size; y++) {
    const dy = Math.min(y, size - y);
    for (let x = 0; x < size; x++) {
      const dx = Math.min(x, size - x);
      k[y * size + x] = Math.exp(-(dx * dx + dy * dy) / (2 * sigma * sigma));
    }
  }
  const energy = new Float64Array(n);
  const add = (i: number, s: number) => {
    const ix = i % size, iy = (i / size) | 0;
    for (let y = 0; y < size; y++) {
      const ky = ((y - iy + size) % size) * size;
      const row = y * size;
      // x < ix: kernel column x - ix + size; x >= ix: x - ix
      for (let x = 0; x < ix; x++) energy[row + x]! += s * k[ky + x - ix + size]!;
      for (let x = ix; x < size; x++) energy[row + x]! += s * k[ky + x - ix]!;
    }
  };
  const tightest = (bits: Uint8Array) => {
    let best = -1, e = -Infinity;
    for (let i = 0; i < n; i++) if (bits[i] && energy[i]! > e) { e = energy[i]!; best = i; }
    return best;
  };
  const largestVoid = (bits: Uint8Array) => {
    let best = -1, e = Infinity;
    for (let i = 0; i < n; i++) if (!bits[i] && energy[i]! < e) { e = energy[i]!; best = i; }
    return best;
  };
  // seeded random initial pattern, about 10 % ones
  let s = seed >>> 0 || 1;
  const rnd = () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
  const bits = new Uint8Array(n);
  const ones = Math.max(1, Math.round(n / 10));
  for (let c = 0; c < ones; ) {
    const i = Math.floor(rnd() * n);
    if (!bits[i]) { bits[i] = 1; add(i, 1); c++; }
  }
  // spread the initial ones: move the tightest cluster into the largest void until that does not move anything
  for (let it = 0; it < n; it++) {
    const t = tightest(bits);
    bits[t] = 0; add(t, -1);
    const v = largestVoid(bits);
    bits[v] = 1; add(v, 1);
    if (v === t) break;
  }
  const rank = new Uint16Array(n);
  const proto = bits.slice(), protoEnergy = energy.slice();
  // phase 1: remove the ones from the tightest cluster down, ranks ones-1 .. 0
  for (let r = ones - 1; r >= 0; r--) {
    const t = tightest(bits);
    bits[t] = 0; add(t, -1);
    rank[t] = r;
  }
  // phases 2 and 3: from the initial pattern, fill the largest void, ranks ones .. n-1 (the zero with the most
  // energy among zeros is the zero with the least energy among ones, so one rule covers both halves)
  bits.set(proto); energy.set(protoEnergy);
  for (let r = ones; r < n; r++) {
    const v = largestVoid(bits);
    bits[v] = 1; add(v, 1);
    rank[v] = r;
  }
  return rank;
}

let cached: Uint8Array | null = null;

/** The 64 x 64 map as bytes: rank * 256 / 4096, so each byte value appears 16 times. */
export function blueNoise64(): Uint8Array {
  if (!cached) {
    const r = voidAndCluster(64);
    cached = new Uint8Array(r.length);
    for (let i = 0; i < r.length; i++) cached[i] = r[i]! >> 4;
  }
  return cached;
}
